import * as React from 'react';
import { BREAKPOINTS } from './types.js';
import { CDN_BASE, BUCKETS, SUPPORTED_DENSITIES } from './constants.js';

/**
 * convert an index 0..3 return the appropriate value from the given BreakpointConfiguration
 */

var indexToBreakpointConfig = function indexToBreakpointConfig(index, config) {
  switch (index) {
    case 0:
      return config.Mobile;

    case 1:
      return config.Tablet;

    case 2:
      return config.Desktop;

    default:
      return config.WideScreen;
  }
};
/**
 * return the smallest image dimension for the given required size. If there is none
 * just return our largest allowed image.
 */

var closestSizeBucket = function closestSizeBucket(size) {
  // zero size return 0
  if (size <= 0) {
    return 0;
  }

  for (var i = 0; i < BUCKETS.length; i += 1) {
    if (BUCKETS[i] >= size) {
      return BUCKETS[i];
    }
  } // nothing ideal so return the largest available image


  return BUCKETS[BUCKETS.length - 1];
};
/**
 * In many context the URL may already be encoded for resizing/formatting from our CDN.
 * We wish to supply our own formatting. This function extracts the source URL from
 * these types of strings. If the URL does not contain a CDN transform then the
 * original URI is returned.
 *
 * The regex project is here: https://regex101.com/r/sbpWpC/1/
 */

var urlExtractRegex = /^(?:https?|localhost):(?:\d+)?\/\/.+(http(?:s)?:(\d+)?\/\/.+)$/;
var getCannonicalURI = function getCannonicalURI(source) {
  var match = urlExtractRegex.exec(source);

  if (match) {
    // return the source URI which follows the CDN transform URI
    return match[1];
  } // we assume the URI references the image directly


  return source;
};
/**
 * This is a key function. It generates the <source /> elements for each breakpoint. These
 * are rendered within the <picture /> element to generate the responsive set
 */

var sourceSets = function sourceSets(sourceURI, props) {
  var height = props.height,
      viewportPercentages = props.viewportPercentages,
      fixedWidths = props.fixedWidths; // generate a <source /> element for each breakpoint

  var srcSets = BREAKPOINTS.map(function (width, index) {
    // get the required width of the image without density adjustments for this breakpoint.
    var requiredWidth = requiredWidthAtBreakpoint(index, viewportPercentages, fixedWidths); // you can specify an absolute width or a width defined by an aspect ratio.

    var requiredHeight = requiredHeightFromWidth(requiredWidth, height); // for the lower width breakpoints we use a max-width media query on the <source /> element
    // to constrain it to that breakpoint. For the last ( extra large ) breakpoint we use a min-width
    // query on the <source /> element so that is applies to any width at or above the ending width of the previous breakpoint

    var mediaQuery = index === BREAKPOINTS.length - 1 ? "(min-width: ".concat(BREAKPOINTS[index - 1], "px)") : "(max-width: ".concat(width, "px)");
    return /*#__PURE__*/React.createElement("source", {
      srcSet: densityTransforms(sourceURI, requiredWidth, requiredHeight, props),
      media: mediaQuery,
      key: width
    });
  });
  return srcSets;
};
/**
 * If a height if specified calculate based on given width. If not height is specified
 * then just return 0. No height will be passed to the CDN transform.
 */

var requiredHeightFromWidth = function requiredHeightFromWidth(width, height) {
  if (height) {
    // height might a literal or an aspect ratio { width: 3, height: 2}
    if (isFinite(height)) {
      return height;
    }

    var _ref = height,
        aspectWidth = _ref.aspectWidth,
        aspectHeight = _ref.aspectHeight;
    return aspectHeight / aspectWidth * width;
  }

  return 0;
};
/**
 * Checks if a URL is valid
 */

var isValidURL = function isValidURL(url) {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};
/**
 * Regex to parse out the resize options to enable us to get the actual path to the image
 * within the static bucket: https://regex101.com/r/83wVaJ/2
 *
 * For example, if user passes in `https://img.cdn4dd.com/p/fit=contain,format=auto/media/photosV2/10c4ceeb-f47f-43db-8e61-567c1c554c9f-retina-large.JPEG`,
 * this regex allows us to grab the `/media/photosV2/10c4ceeb-f47f-43db-8e61-567c1c554c9f-retina-large.JPEG` to apply the new
 * resize options that have been passed through the component
 */

var resizeOptionsRegex = /\/(?:[a-zA-Z]+=\w+,?)+/;
/**
 * Given a base uri, desired width and display density ( optional ) return a URL that will
 * deliver that image from the CDN
 * If a height is specified it will be used or calculated from the aspect ratio
 * e.g.
 *
 * given
 * "ABC.JPG", 400, 2
 * return
 * "https://img.cdn4dd.com/p/fit=contain,width=400,format=auto,quality=50/ABC.JPG 2x"
 */

var formatURL = function formatURL(baseURL, width, height, density, props) {
  var imgPath;

  try {
    // Extract path from URL saved in DB; includes leading '/'
    imgPath = new URL(baseURL).pathname;
  } catch (ex) {
    // Return emtpy string if baseURL is invalid/empty
    return '';
  } // If they passed in a url using the static `/s` path, we need to remove the `/s
  // because the `/p` resize service is pulling from `/s` under the hood


  if (imgPath.indexOf('/s') === 0) {
    imgPath = imgPath.split('/s')[1];
  }

  if (imgPath.indexOf('/p') === 0) {
    imgPath = imgPath.split(resizeOptionsRegex)[1];
  }

  var quality = props.quality,
      objectFit = props.objectFit,
      format = props.format;
  var optionalHeight = height ? "height=".concat(height, ",") : ''; // optional density

  var densityStr = density ? "".concat(density, "x") : ''; // full new URL

  return "".concat(CDN_BASE, "fit=").concat(objectFit, ",width=").concat(width, ",").concat(optionalHeight, "format=").concat(format, ",quality=").concat(quality).concat(imgPath, " ").concat(densityStr);
};
/**
 * generates density transforms for all supported pixel densities
 * e.g. 'https://p/p1.jpg?transform=xyz 1x, https://p/p1.jpg?transform=xyz 2x, https://p/p1.jpg?transform=xyz 3x'
 */

var densityTransforms = function densityTransforms(sourceURI, width, height, props) {
  var maximumDensity = props.maximumDensity; // filter supported densities to ensure we don't exceed the maximum required

  var densities = SUPPORTED_DENSITIES.filter(function (density) {
    return density <= maximumDensity;
  });
  return densities.map(function (density) {
    return formatURL(sourceURI, closestSizeBucket(width * density), closestSizeBucket(height * density), density, props);
  }).join(', ');
};
/**
 * return the required width of the image WITHOUT adjustment for display density
 * for the breakpoint at the given index
 */

var requiredWidthAtBreakpoint = function requiredWidthAtBreakpoint(index, viewportPercentages, fixedWidths) {
  if (viewportPercentages) {
    // get the maximum width of the breakpoint
    var breakpointWidth = BREAKPOINTS[index]; // get the viewport percentage for this breakpoint

    var viewportPercentage = indexToBreakpointConfig(index, viewportPercentages); // return the actual width of image at this breakpoint

    return breakpointWidth * (viewportPercentage / 100 / 1);
  } // if using fixed widths then we just return the given width


  return indexToBreakpointConfig(index, fixedWidths);
};

export { closestSizeBucket, densityTransforms, formatURL, getCannonicalURI, indexToBreakpointConfig, isValidURL, requiredHeightFromWidth, requiredWidthAtBreakpoint, sourceSets };
